import { contentStateFromString, contentStateToString } from "@utils/richText/converters";
import { ApplicationDto, applicationFromDto } from "@domain/application/dtos";
import { Carousel } from "@domain/carousel/carousel";
import { CarouselDto, carouselFromDto, CarouselPostInfos } from "@domain/carousel/dtos";
import { GroupDto, groupFromDto } from "@domain/group/dtos";
import { LivecourseDto, livecourseFromDto } from "@domain/livecourse/dtos";
import { ArticleDto, articleFromDto } from "@domain/lms/article/dtos";
import { ContentLanguage } from "@domain/lms/contentLanguage";
import { CourseDto, courseFromDto } from "@domain/lms/course/dtos";
import { QuizDto, quizFromDto } from "@domain/lms/quiz/dto";
import { MediaDto, mediaFromDto } from "@domain/media/dtos";
import { OrganizationDto, organizationFromDto } from "@domain/organization/dtos";
import { PersonDto, personFromDto } from "@domain/user/dtos";
import { PostCommentDto, postCommentFromDto } from "@domain/post/comment/dtos";
import { ActionInfo, Post } from "./post";
import { PostDraft } from "./postDraft";

export function postFromDto(dto: PostDto): Post {
	const { author, type, text, reference, assets, ...base } = dto;

	return {
		...base,
		author: personFromDto(author),
		text: contentStateFromString(text ?? ""),
		postDescription: contentStateFromString(text ?? "").getPlainText(),
		creationDate: new Date(dto.creationDate),
		assets: assets.map((asset) => mediaFromDto(asset)),
		assetUrl: assets.map((asset) => mediaFromDto(asset))[0]?.url ?? "",
		lastUpdateDate: new Date(dto.lastUpdateDate),
		comments: { ...dto.comments, last: dto.comments.last && postCommentFromDto(dto.comments.last) },
		...(type === "SHARE_POST"
			? {
					reference: reference && postFromDto(reference as PostDto),
					type,
				}
			: type === "SHARE_COURSE"
				? {
						reference: reference && courseFromDto(reference as CourseDto),
						type,
					}
				: type === "SHARE_LIVE_COURSE"
					? {
							reference: reference && livecourseFromDto(reference as LivecourseDto),
							type,
						}
					: type === "SHARE_APPLICATION"
						? {
								reference: reference && applicationFromDto(reference as ApplicationDto),
								type,
							}
						: type === "SHARE_ARTICLE"
							? {
									reference: reference && articleFromDto(reference as ArticleDto),
									type,
								}
							: type === "SHARE_QUIZ"
								? {
										reference: reference && quizFromDto(reference as QuizDto),
										type,
									}
								: type === "SHARE_QUIZ_RESULTS"
									? {
											reference: reference && {
												...quizFromDto(reference as QuizWithScoreDto),
												userScore: (reference as QuizWithScoreDto).userScore,
											},
											type,
										}
									: type === "SHARE_ORGANIZATION"
										? {
												reference: reference && {
													...organizationFromDto(reference as OrganizationDto),
												},
												type,
											}
										: type === "SHARE_GROUP"
											? {
													reference: reference && {
														...groupFromDto(reference as GroupDto),
													},
													type,
												}
											: type === "SHARE_CAROUSEL"
												? {
														assets: [],
														reference: reference && {
															...carouselFromDto(reference as Carousel),
														},
														type,
													}
												: {
														type,
													}),
	};
}

export function draftToDto(draft: PostDraft): PostDraftDto {
	return {
		...draft,
		...carouselInfosToDto(draft.carouselDetails),
		text: contentStateToString(draft.text),
		assetIds: draft.assets.map(({ id }) => id),
	};
}

export function carouselInfosToDto(carouselInfosDraft: CarouselPostInfos | undefined): CarouselDto {
	return !carouselInfosDraft
		? { isCarrousel: false }
		: {
				isCarrousel: true,
				title: carouselInfosDraft.title,
				category: carouselInfosDraft.category,
				language: carouselInfosDraft.language as ContentLanguage,
			};
}

export interface PostDto extends Omit<
	Post,
	"author" | "text" | "creationDate" | "lastUpdateDate" | "reference" | "comments" | "assets"
> {
	author: PersonDto;
	text?: string;
	lastUpdateDate: string;
	creationDate: string;
	reference?:
		| PostDto
		| CourseDto
		| QuizDto
		| (QuizDto & { userScore: number })
		| OrganizationDto
		| GroupDto
		| ArticleDto
		| LivecourseDto
		| Carousel
		| ApplicationDto;
	comments: ActionInfo & { last?: PostCommentDto };
	assets: MediaDto[];
}

export interface QuizWithScoreDto extends QuizDto {
	userScore: number;
}
export interface PostDraftDto {
	text: string;
	assetIds: string[];
}
